import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import {
	Area,
	AreaChart,
	CartesianGrid,
	Legend,
	ReferenceArea,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';

const height = 400;

const styles = () => {
	return {
		container: {
			position: 'relative',
			marginTop: 20,
			padding: 10,
			boxShadow:
				`0px 1px 5px 0px rgba(0, 0, 0, 0.2),` +
				`0px 2px 2px 0px rgba(0, 0, 0, 0.14),` +
				`0px 3px 1px -2px rgba(0, 0, 0, 0.12)`,
			borderRadius: 2,
			backgroundColor: '#f0f0f0',
			minHeight: 400,
		},
		emptyEstimate: {
			height: height,
			width: '100%',
			backgroundPosition: 'center',
			backgroundSize: 'cover',
			display: 'flex',
			justifyContent: 'center',
			flexDirection: 'column',
			textAlign: 'center',
			fontSize: 20,
			fontWeight: 100,
			color: '#666',
		},
		isFetching: {
			position: 'absolute',
			left: 0,
			top: 0,
			zIndex: 100,
			height: '100%',
			width: '100%',
			backgroundColor: 'rgba(240,240,240,.9)',
			display: 'flex',
			justifyContent: 'center',
			flexDirection: 'column',
			textAlign: 'center',
			fontSize: 20,
			fontWeight: 100,
			color: '#666',
		},
	};
};

class StationDeliveryEstimate extends Component {
	render() {
		const { classes, deliveryEstimate, isFetching } = this.props;
		//const {} = this.state;

		const fetchingDiv = !isFetching ? null : (
			<div className={classes.isFetching}>
				<div>
					<CircularProgress size={150} />
				</div>
			</div>
		);

		if (!deliveryEstimate) {
			return (
				<div className={classes.container}>
					{fetchingDiv}
					<div className={classes.emptyEstimate}>
						Choose a station
						<br />
						to display delivery estimate
					</div>
				</div>
			);
		}

		/** @namespace deliveryEstimate.inventories */
		/** @namespace deliveryEstimate.estimatedInventories */
		const {
			tankAt90Percent,
			shutoffVolume,
			resupplyVolume,
			totalTankVolume,
			inventories,
			recommendedStartWindow,
			recommendedEndWindow,
			errors,
		} = deliveryEstimate;

		if (!recommendedStartWindow) {
			return (
				<div className={classes.container}>
					{fetchingDiv}
					{_.isEmpty(errors) && (
						<div className={classes.emptyEstimate}>
							Unable to perform estimate.
							<br />
							This is mostly likely due to not having veeder readings for this station.
						</div>
					)}

					{!_.isEmpty(errors) && (
						<div className={classes.emptyEstimate}>
							Unable to perform estimate.
							<br />
							{errors.map((error, i) => (
								<span key={i}>{error}</span>
							))}
						</div>
					)}
				</div>
			);
		}

		const startEpoch = moment(recommendedStartWindow).unix();
		const endEpoch = moment(recommendedEndWindow).unix();
		const skipXEpochs = [startEpoch, endEpoch];

		const nowEpoch = moment().unix();
		//let deliveryMoment = undefined;
		//let deliveryEpoch = undefined;
		let chartRows = [];
		inventories.forEach((inv) => {
			if (!inv.volume && !inv.estimatedVolume && !inv.deliveryVolume) {
				return;
			}

			const epoch = moment(inv.inventoryDateTime || inv.date).unix();
			chartRows.push({
				estimatedVolume: inv.estimatedVolume,
				inventoryVolume: inv.volume,
				deliveryVolume: inv.deliveryVolume,
				isWindow: inv.isWindow,
				isNotStartOfDay: inv.isNotStartOfDay,
				epoch,
			});

			if (inv.isNotStartOfDay) {
				skipXEpochs.push(epoch);
			}
		});

		chartRows = _.orderBy(chartRows, 'epoch');
		const deliveryColor = '#CD79C5';

		return (
			<div className={classes.container}>
				{fetchingDiv}
				<ResponsiveContainer width="100%" height={height} style={{ paddingBottom: 20 }}>
					<AreaChart data={chartRows} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
						<defs>
							<linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
								<stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
							</linearGradient>
							<linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
								<stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
							</linearGradient>

							<linearGradient id="colorDelivery" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor={deliveryColor} stopOpacity={0.8} />
								<stop offset="95%" stopColor={deliveryColor} stopOpacity={0} />
							</linearGradient>
						</defs>
						<Tooltip content={<CustomTooltip />} />
						<XAxis
							tick={<CustomizedAxisTick skipXEpochs={skipXEpochs} />}
							fontSize={12}
							dataKey="epoch"
							type="number"
							domain={['dataMin', 'dataMax']}
							interval={0}
							scale="time"
						/>
						<YAxis
							fontSize={12}
							allowDataOverflow={true}
							type="number"
							domain={[0, totalTankVolume]}
						/>
						<CartesianGrid strokeDasharray="3 3" />
						<Tooltip />
						<Legend verticalAlign="top" height={36} />

						<ReferenceLine
							alwaysShow={undefined}
							x={nowEpoch}
							stroke="#bbbbbb"
							strokeWidth={2}
							label={{
								value: 'now',
								position: 'top',
								fill: '#888',
								fontSize: 10,
							}}
						/>

						{/*
						<ReferenceArea
							y1={0}
							y2={shutoffVolume}
							fill="#F44337"
							fillOpacity={0.2}
							alwaysShow={undefined}
							label={{
								value: `Shutoff (${shutoffVolume})`,
								fill: 'black',
								fontSize: 12,
								position: 'top',
							}}
						/>

						*/}

						{/* Resupply & shutoff line */}
						<ReferenceLine
							alwaysShow={undefined}
							y={resupplyVolume}
							stroke="#F44337"
							strokeWidth={1}
							label=""
						/>
						<ReferenceArea
							y1={0}
							y2={resupplyVolume}
							fill="#F44337"
							fillOpacity={0.2}
							alwaysShow={undefined}
							label={{
								value: `Resupply (${resupplyVolume})`,
								fill: 'black',
								fontSize: 13,
								position: 'insideTop',
							}}
						/>
						<ReferenceLine
							alwaysShow={undefined}
							y={shutoffVolume}
							stroke="#444"
							strokeWidth={1}
							strokeDasharray="5 5"
							label={{
								value: `Shutoff (${shutoffVolume})`,
								fill: 'black',
								fontSize: 12,
								position: 'insideTopLeft',
							}}
						/>
						<ReferenceLine
							alwaysShow={undefined}
							x={startEpoch}
							stroke="#2B96F3"
							strokeWidth={1}
							label={<ReferenceLabel startEpoch={startEpoch} endEpoch={endEpoch} />}
						/>
						<ReferenceLine alwaysShow={undefined} x={endEpoch} stroke="#2B96F3" strokeWidth={1} />
						<ReferenceArea
							alwaysShow={undefined}
							x1={startEpoch}
							x2={endEpoch}
							fill="#2B96F3"
							fillOpacity={0.2}
							label={{
								value: `window`,
								fill: '#666',
								fontSize: 11,
								position: 'top',
							}}
						/>

						<ReferenceLine
							alwaysShow={undefined}
							y={tankAt90Percent}
							stroke="#F44337"
							strokeWidth={1}
							label=""
						/>
						<ReferenceArea
							alwaysShow={undefined}
							y1={totalTankVolume}
							y2={tankAt90Percent}
							fill="#F44337"
							fillOpacity={0.2}
							stroke={'1px solid top'}
							label={{
								value: `Tank 90% (${tankAt90Percent})`,
								fill: 'black',
								fontSize: 14,
							}}
						/>
						<Area
							type="monotone"
							name="Readings"
							dataKey="inventoryVolume"
							stroke="#82ca9d"
							fillOpacity={1}
							fill="url(#colorPv)"
						/>
						<Area
							type="monotone"
							name="Projected"
							dataKey="estimatedVolume"
							stroke="#8884d8"
							fillOpacity={1}
							fill="url(#colorUv)"
						/>

						<Area
							type="monotone"
							name="Projected Delivery"
							dataKey="deliveryVolume"
							stroke={deliveryColor}
							fillOpacity={1}
							fill="url(#colorDelivery)"
						/>
					</AreaChart>
				</ResponsiveContainer>
			</div>
		);
	}
}

class CustomizedAxisTick extends Component {
	render() {
		const { x, y, payload, skipXEpochs } = this.props;
		const m = moment.unix(payload.value);

		if (_.includes(skipXEpochs, payload.value)) {
			return null;
		}

		return (
			<text fontSize={12} x={x} y={y} dy={16} textAnchor="middle" fill="#666">
				<tspan x={x} dy="1.2em">
					{m.format('M/D')}
				</tspan>
				<tspan x={x} dy="1.2em">
					{m.format('ha')}
				</tspan>
			</text>
		);
	}
}

class CustomTooltip extends Component {
	render() {
		const { active, payload, label } = this.props;
		if (!active || !payload || !payload.length) {
			return null;
		}

		const m = moment.unix(label);
		const date = m.format('ddd, MMM Do @ h:mma');
		//const volume = payload[0].value;

		const labelMap = {
			estimatedVolume: 'Projected',
			inventoryVolume: 'Inventory',
			deliveryVolume: 'Proj. Delivery',
		};

		return (
			<div
				style={{
					backgroundColor: 'rgba(255,255,255,.8)',
					padding: 10,
					borderRadius: 3,
					color: '#555',
					fontSize: 13,
					lineHeight: 1.2,
					margin: 0,
					boxShadow:
						`0px 1px 5px 0px rgba(0, 0, 0, 0.2),` +
						`0px 2px 2px 0px rgba(0, 0, 0, 0.14),` +
						`0px 3px 1px -2px rgba(0, 0, 0, 0.12)`,
				}}
			>
				<div>{`${date}`}</div>
				{_.map(payload, (sub, i) => {
					return (
						<div key={i}>
							<div>
								{labelMap[sub.dataKey]} : <strong>{`${sub.value} gallons`}</strong>
							</div>
						</div>
					);
				})}
			</div>
		);
	}
}

CustomTooltip.propTypes = {
	type: PropTypes.string,
	payload: PropTypes.array,
	label: PropTypes.number,
	active: PropTypes.bool,
};

CustomizedAxisTick.propTypes = {
	skipXEpochs: PropTypes.array,
	x: PropTypes.number,
	y: PropTypes.number,
	payload: PropTypes.object,
};

export function ReferenceLabel(props) {
	const { viewBox, dy, dx, startEpoch, endEpoch } = props;
	const x = viewBox.width + viewBox.x - 5;
	const y = viewBox.y + 100;

	if (!startEpoch || !endEpoch) {
		return <text />;
	}

	const start = moment.unix(startEpoch);
	const end = moment.unix(endEpoch);

	let line1;
	let line2;
	if (start.date() === end.date()) {
		const firstTime = start.minute() === 0 ? start.format('h') : start.format('h:mm');

		const secondTime = end.minute() === 0 ? end.format('h') : end.format('h:mm');

		let time;
		const sampm = start.format('a');
		const eampm = end.format('a');

		if (sampm === eampm) {
			time = `(${firstTime} - ${secondTime} ${sampm})`;
		} else {
			time = `(${firstTime} ${sampm} - ${secondTime} ${eampm})`;
		}

		line1 = `${start.format('ddd M/DD')}`;
		line2 = time;
	} else {
		line1 = `${start.format('ddd M/DD h:mm a')} -`;
		line2 = `${end.format('ddd M/DD h:mm a')}`;
	}

	return (
		<text x={x} y={y} dy={dy} dx={dx} fontSize={12} textAnchor={'end'}>
			<tspan x={x} dy="1.2em">
				Delivery
			</tspan>
			<tspan x={x} dy="1.2em">
				{line1}
			</tspan>
			<tspan x={x} dy="1.2em">
				{line2}
			</tspan>
		</text>
	);
}

ReferenceLabel.propTypes = {
	viewBox: PropTypes.object,
	dy: PropTypes.number,
	dx: PropTypes.number,
	startEpoch: PropTypes.number,
	endEpoch: PropTypes.number,
};

StationDeliveryEstimate.propTypes = {
	classes: PropTypes.object.isRequired,
	deliveryEstimate: PropTypes.object,
	isFetching: PropTypes.bool,
};

export default _.flow([withStyles(styles)])(StationDeliveryEstimate);
